import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react';
import { api, setAccessToken, refreshSession } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrapSession = useCallback(async () => {
    try {
      try {
        const data = await refreshSession();
        if (data?.success && data.user && data.token) {
          setUser(data.user);
          return;
        }
      } catch {
        // Cookie refresh can fail on reload; fall back to the stored access token.
      }

      try {
        const me = await api.get('/auth/me');
        if (me.data?.success && me.data.user) {
          setUser(me.data.user);
          return;
        }
      } catch {
        // No valid session
      }

      setAccessToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrapSession();
  }, [bootstrapSession]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });

      if (response.data.success) {
        const { user: userData, token } = response.data;
        if (token) {
          setAccessToken(token);
        }
        setUser(userData);
        return { success: true };
      }

      return {
        success: false,
        error: response.data.message || 'Login failed'
      };
    } catch (error) {
      console.error('AuthContext: Login failed:', error);
      console.error('AuthContext: Error response:', error.response?.data);
      setAccessToken(null);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const loginWithToken = async (token, userFromResponse) => {
    try {
      setAccessToken(token);
      if (userFromResponse) {
        setUser(userFromResponse);
        return { success: true, user: userFromResponse };
      }
      const response = await api.get('/auth/me');
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      }

      setAccessToken(null);
      return { success: false, error: 'Invalid authentication token' };
    } catch (error) {
      console.error('AuthContext: Token login failed:', error);
      setAccessToken(null);
      return {
        success: false,
        error: error.response?.data?.message || 'Token authentication failed'
      };
    }
  };

  const register = async (firstName, lastName, username, email, password) => {
    try {
      const response = await api.post('/auth/register', {
        firstName,
        lastName,
        username,
        email,
        password
      });

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        };
      }

      return {
        success: false,
        error: response.data.message || 'Registration failed'
      };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      setUser(response.data);
      return { success: true };
    } catch (error) {
      console.error('Profile update failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Profile update failed'
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      if (response.data.success) {
        return { success: true };
      }

      return {
        success: false,
        error: response.data.message || 'Password change failed'
      };
    } catch (error) {
      console.error('Password change failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Password change failed'
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        };
      }

      return {
        success: false,
        error: response.data.message || 'Failed to send reset email'
      };
    } catch (error) {
      console.error('Forgot password failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send reset email'
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword
      });
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        };
      }

      return {
        success: false,
        error: response.data.message || 'Password reset failed'
      };
    } catch (error) {
      console.error('Password reset failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Password reset failed'
      };
    }
  };

  const verifyEmail = async (token) => {
    try {
      const response = await api.post('/auth/verify-email', { token });
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        };
      }

      return {
        success: false,
        error: response.data.message || 'Email verification failed'
      };
    } catch (error) {
      console.error('Email verification failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Email verification failed'
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    loginWithToken,
    register,
    logout,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    verifyEmail,
    bootstrapSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

